/**
 * Models Admin View
 *
 * Provider-centric LLM model management.
 * Left panel: connected providers list.
 * Right panel: provider detail with model table, or add/edit form.
 */

import { html, nothing } from "lit";

// --------------- Types ---------------

export type ModelEntry = {
  id: string;
  name: string;
  provider: string;
  contextWindow?: number;
  reasoning?: boolean;
};

export type ProviderEntry = {
  id: string;
  name: string;
  envVar: string;
  connected: boolean;
  modelCount: number;
  enabledCount: number;
  apiProtocol: string;
  description: string;
  local: boolean;
};

export type ProviderFormEntry = {
  providerId: string;
  name: string;
  apiKey: string;
  baseUrl: string;
  envVar: string;
  isNew: boolean;
  modelIds: string;
};

export type EmbeddingProps = {
  model: string | null;
  dimension: number | null;
  status: {
    provider: string;
    model: string | null;
    baseUrl: string | null;
    healthy: boolean;
    error?: string;
  } | null;
  availableModels: Array<{ id: string; owned_by?: string }>;
  loading: boolean;
  saving: boolean;
  onSelectModel: (model: string) => void;
  onSave: (model: string, dimension?: number) => void;
  onRefreshModels: () => void;
};

export type DoclingProps = {
  url: string;
  ocrLanguage: string;
  selectedModel: string | null;
  availableModels: Array<{ id: string; name: string; provider: string }>;
  status: {
    configured: boolean;
    url: string | null;
    healthy: boolean;
    version?: string | null;
    formats?: string[] | null;
    error?: string;
  } | null;
  loading: boolean;
  saving: boolean;
  onSelectModel: (model: string) => void;
  onSave: (url: string, ocrLanguage: string, model: string) => void;
  onRefresh: () => void;
};

export type ModelsAdminProps = {
  loading: boolean;
  error: string | null;
  providers: ProviderEntry[];
  selectedProvider: ProviderEntry | null;
  selectedProviderModels: Array<ModelEntry & { enabled: boolean }>;
  form: ProviderFormEntry | null;
  formDirty: boolean;
  addableProviders: Array<{ id: string; name: string; description: string }>;
  embedding: EmbeddingProps;
  docling: DoclingProps;
  onRefresh: () => void;
  onDiscover: () => void;
  onSelectProvider: (id: string) => void;
  onStartAdd: () => void;
  onStartEdit: (id: string) => void;
  onDisconnect: (id: string) => void;
  onFormChange: (field: string, value: string) => void;
  onFormTypeChange: (providerId: string) => void;
  onSaveForm: () => void;
  onCancelForm: () => void;
  onToggleModel: (fullId: string) => void;
  onEnableAll: () => void;
  onDisableAll: () => void;
};

// --------------- Embedding Section ---------------

function renderEmbeddingSection(emb: EmbeddingProps) {
  const statusColor = emb.status?.healthy
    ? "var(--color-ok, #22c55e)"
    : "var(--color-danger, #ef4444)";
  const statusLabel =
    emb.status == null
      ? "Loading..."
      : emb.status.healthy
        ? "Connected"
        : emb.status.provider === "none"
          ? "Not configured"
          : "Error";

  return html`
    <div class="card" style="margin-bottom:16px;">
      <div
        class="card-title"
        style="display:flex;justify-content:space-between;align-items:center;"
      >
        <span>
          Embedding Model
          <span
            style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${statusColor};margin-left:8px;vertical-align:middle;"
            title="${statusLabel}"
          ></span>
          <span class="muted" style="font-size:0.8em;margin-left:4px;">${statusLabel}</span>
        </span>
        <button
          class="btn btn--sm"
          @click=${emb.onRefreshModels}
          ?disabled=${emb.loading}
          title="Refresh available models from your provider"
        >
          ${emb.loading ? "..." : "Refresh Models"}
        </button>
      </div>

      ${
        emb.status?.error
          ? html`<div class="callout danger" style="margin:8px 0;font-size:0.85em;">
              ${emb.status.error}
            </div>`
          : nothing
      }

      <div style="display:flex;gap:12px;align-items:flex-end;margin-top:12px;flex-wrap:wrap;">
        <!-- Model picker -->
        <div style="flex:1;min-width:240px;">
          <label style="display:block;font-size:0.8em;margin-bottom:4px;color:var(--fg-muted);">
            Model
          </label>
          <select
            style="width:100%;padding:6px 8px;border:1px solid var(--border);border-radius:4px;background:var(--bg);color:var(--fg);font-size:0.9em;"
            .value=${emb.model ?? ""}
            @change=${(e: Event) => {
              const val = (e.target as HTMLSelectElement).value;
              emb.onSelectModel(val);
            }}
          >
            <option value="">-- Select embedding model --</option>
            ${emb.availableModels.map(
              (m) => html`
                <option value=${m.id} ?selected=${m.id === emb.model}>
                  ${m.id}${m.owned_by ? ` (${m.owned_by})` : ""}
                </option>
              `,
            )}
            ${
              emb.model && !emb.availableModels.some((m) => m.id === emb.model)
                ? html`<option value=${emb.model} selected>${emb.model} (current)</option>`
                : nothing
            }
          </select>
        </div>

        <!-- Dimension -->
        <div style="width:120px;">
          <label style="display:block;font-size:0.8em;margin-bottom:4px;color:var(--fg-muted);">
            Dimension
          </label>
          <input
            type="number"
            style="width:100%;padding:6px 8px;border:1px solid var(--border);border-radius:4px;background:var(--bg);color:var(--fg);font-size:0.9em;"
            .value=${String(emb.dimension ?? 1536)}
            placeholder="1536"
            id="embedding-dimension-input"
          />
        </div>

        <!-- Save -->
        <button
          class="btn btn--sm btn--primary"
          style="height:34px;"
          ?disabled=${emb.saving || !emb.model}
          @click=${() => {
            const dimInput = document.getElementById(
              "embedding-dimension-input",
            ) as HTMLInputElement | null;
            const dim = dimInput ? Number.parseInt(dimInput.value, 10) : undefined;
            emb.onSave(emb.model ?? "", Number.isNaN(dim!) ? undefined : dim);
          }}
        >
          ${emb.saving ? "Saving..." : "Save"}
        </button>
      </div>

      <div class="muted" style="font-size:0.75em;margin-top:8px;">
        Select an embedding model from your configured provider.
        Changes are applied immediately without restarting the gateway.
      </div>
    </div>
  `;
}

// --------------- Docling Section ---------------

const OCR_LANGUAGES: ReadonlyArray<{ code: string; name: string }> = [
  { code: "eng", name: "English" },
  { code: "ara", name: "Arabic" },
  { code: "chi_sim", name: "Chinese (Simplified)" },
  { code: "chi_tra", name: "Chinese (Traditional)" },
  { code: "deu", name: "German" },
  { code: "fra", name: "French" },
  { code: "heb", name: "Hebrew" },
  { code: "hin", name: "Hindi" },
  { code: "ita", name: "Italian" },
  { code: "jpn", name: "Japanese" },
  { code: "kor", name: "Korean" },
  { code: "nld", name: "Dutch" },
  { code: "pol", name: "Polish" },
  { code: "por", name: "Portuguese" },
  { code: "rus", name: "Russian" },
  { code: "spa", name: "Spanish" },
  { code: "tha", name: "Thai" },
  { code: "tur", name: "Turkish" },
  { code: "ukr", name: "Ukrainian" },
  { code: "vie", name: "Vietnamese" },
];

function renderDoclingSection(doc: DoclingProps) {
  const statusColor = doc.status?.healthy
    ? "var(--color-ok, #22c55e)"
    : doc.status?.configured
      ? "var(--color-danger, #ef4444)"
      : "var(--color-muted, #888)";
  const statusLabel =
    doc.status == null
      ? "Loading..."
      : doc.status.healthy
        ? "Connected"
        : doc.status.configured
          ? "Unreachable"
          : "Not configured";

  const SUPPORTED_FORMATS = [
    { ext: "PDF", note: "tables, layout, OCR" },
    { ext: "DOCX", note: "Word documents" },
    { ext: "PPTX", note: "PowerPoint (Docling only)" },
    { ext: "XLSX", note: "Excel (Docling only)" },
    { ext: "HTML", note: "web pages" },
    { ext: "Images", note: "OCR via Tesseract" },
    { ext: "LaTeX", note: "Docling only" },
  ];

  return html`
    <div class="card" style="margin-bottom:16px;">
      <div
        class="card-title"
        style="display:flex;justify-content:space-between;align-items:center;"
      >
        <span>
          Document Processing (Docling)
          <span
            style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${statusColor};margin-left:8px;vertical-align:middle;"
            title="${statusLabel}"
          ></span>
          <span class="muted" style="font-size:0.8em;margin-left:4px;">${statusLabel}</span>
          ${
            doc.status?.healthy && doc.status.version
              ? html`<span class="muted" style="font-size:0.75em;margin-left:6px;">
                  v${doc.status.version}
                </span>`
              : nothing
          }
        </span>
        <button
          class="btn btn--sm"
          @click=${doc.onRefresh}
          ?disabled=${doc.loading}
          title="Check Docling service health"
        >
          ${doc.loading ? "..." : "Check Status"}
        </button>
      </div>

      ${
        doc.status?.error
          ? html`<div class="callout danger" style="margin:8px 0;font-size:0.85em;">
              ${doc.status.error}
            </div>`
          : nothing
      }

      <!-- LLM Model picker -->
      <div style="display:flex;gap:12px;align-items:flex-end;margin-top:12px;flex-wrap:wrap;">
        <div style="flex:1;min-width:240px;">
          <label style="display:block;font-size:0.8em;margin-bottom:4px;color:var(--fg-muted);">
            Model
          </label>
          <select
            style="width:100%;padding:6px 8px;border:1px solid var(--border);border-radius:4px;background:var(--bg);color:var(--fg);font-size:0.9em;"
            .value=${doc.selectedModel ?? ""}
            @change=${(e: Event) => {
              doc.onSelectModel((e.target as HTMLSelectElement).value);
            }}
          >
            <option value="" ?selected=${!doc.selectedModel}>-- Select LLM model --</option>
            ${doc.availableModels.map(
              (m) => html`
                <option
                  value="${m.provider}/${m.id}"
                  ?selected=${`${m.provider}/${m.id}` === doc.selectedModel || m.id === doc.selectedModel}
                >
                  ${m.name} (${m.provider})
                </option>
              `,
            )}
            ${
              doc.selectedModel &&
              !doc.availableModels.some(
                (m) => `${m.provider}/${m.id}` === doc.selectedModel || m.id === doc.selectedModel,
              )
                ? html`<option value=${doc.selectedModel} selected>${doc.selectedModel} (current)</option>`
                : nothing
            }
          </select>
        </div>
      </div>

      <!-- URL + OCR Language + Save -->
      <div style="display:flex;gap:12px;align-items:flex-end;margin-top:12px;flex-wrap:wrap;">
        <!-- URL input -->
        <div style="flex:1;min-width:280px;">
          <label style="display:block;font-size:0.8em;margin-bottom:4px;color:var(--fg-muted);">
            Docling Service URL
          </label>
          <input
            type="text"
            id="docling-url-input"
            style="width:100%;padding:6px 8px;border:1px solid var(--border);border-radius:4px;background:var(--bg);color:var(--fg);font-size:0.9em;"
            .value=${doc.url}
            placeholder="http://localhost:8001"
          />
        </div>

        <!-- OCR Language -->
        <div style="min-width:200px;">
          <label style="display:block;font-size:0.8em;margin-bottom:4px;color:var(--fg-muted);">
            OCR Language
          </label>
          <select
            id="docling-ocr-lang"
            style="width:100%;padding:6px 8px;border:1px solid var(--border);border-radius:4px;background:var(--bg);color:var(--fg);font-size:0.9em;"
          >
            ${OCR_LANGUAGES.map(
              (l) => html`
                <option value=${l.code} ?selected=${l.code === doc.ocrLanguage}>
                  ${l.name} (${l.code})
                </option>
              `,
            )}
          </select>
        </div>

        <!-- Save -->
        <button
          class="btn btn--sm btn--primary"
          style="height:34px;"
          ?disabled=${doc.saving}
          @click=${() => {
            const urlInput = document.getElementById(
              "docling-url-input",
            ) as HTMLInputElement | null;
            const langSelect = document.getElementById(
              "docling-ocr-lang",
            ) as HTMLSelectElement | null;
            const url = urlInput?.value?.trim() ?? "http://localhost:8001";
            const lang = langSelect?.value ?? "eng";
            doc.onSave(url, lang, doc.selectedModel ?? "");
          }}
        >
          ${doc.saving ? "Saving..." : "Save"}
        </button>
      </div>

      <!-- Supported formats -->
      <div style="margin-top:12px;">
        <div style="font-size:0.8em;color:var(--fg-muted);margin-bottom:6px;">
          Supported formats:
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:6px;">
          ${SUPPORTED_FORMATS.map(
            (f) => html`
              <span
                style="display:inline-block;padding:2px 8px;border:1px solid var(--border);border-radius:4px;font-size:0.78em;color:var(--fg-muted);"
                title="${f.note}"
              >
                ${f.ext}
              </span>
            `,
          )}
        </div>
      </div>

      <div class="muted" style="font-size:0.75em;margin-top:8px;">
        Docling is a Python sidecar service for advanced document conversion.
        It provides superior PDF table extraction, PPTX/XLSX support, and MLX-accelerated OCR.
        Changes are applied immediately without restarting the gateway.
      </div>
    </div>
  `;
}

// --------------- Main ---------------

export function renderModelsAdmin(props: ModelsAdminProps) {
  const connectedCount = props.providers.length;

  return html`
    ${renderEmbeddingSection(props.embedding)}
    ${renderDoclingSection(props.docling)}

    <section class="users-layout">
      <!-- Left: Providers -->
      <div class="card">
        <div
          class="card-title"
          style="display:flex;justify-content:space-between;align-items:center;"
        >
          <span>
            LLM Providers
            <span class="muted">(${connectedCount} connected)</span>
          </span>
          <div style="display:flex;gap:6px;">
            <button class="btn btn--sm btn--primary" @click=${props.onStartAdd}>
              + Add
            </button>
            <button
              class="btn btn--sm"
              @click=${props.onDiscover}
              ?disabled=${props.loading}
              title="Poll provider endpoints for available models"
            >
              ${props.loading ? "..." : "Discover"}
            </button>
            <button
              class="btn btn--sm"
              @click=${props.onRefresh}
              ?disabled=${props.loading}
            >
              ${props.loading ? "..." : "Refresh"}
            </button>
          </div>
        </div>

        ${
          props.error
            ? html`<div class="callout danger" style="margin:8px 0;">
              ${props.error}
            </div>`
            : nothing
        }

        <!-- Connected providers -->
        <div style="margin-top:12px;max-height:420px;overflow-y:auto;">
          ${
            props.providers.length === 0 && !props.loading
              ? html`
                  <div class="muted" style="padding: 20px; text-align: center; font-size: 0.9em">
                    No providers connected yet. Click "+ Add" to get started.
                  </div>
                `
              : props.providers.map((p) => renderProviderCard(p, props))
          }
        </div>

      </div>

      <!-- Right: Detail / Form / Empty -->
      <div class="card">
        ${
          props.form
            ? renderProviderForm(props)
            : props.selectedProvider
              ? renderProviderDetail(props)
              : html`
                  <div class="muted" style="padding: 40px; text-align: center; font-size: 0.9em">
                    Select a provider to view its models, or click "+ Add" to connect a new one.
                  </div>
                `
        }
      </div>
    </section>
  `;
}

// --------------- Provider Card ---------------

function renderProviderCard(provider: ProviderEntry, props: ModelsAdminProps) {
  const isSelected = props.selectedProvider?.id === provider.id;
  const badge =
    provider.enabledCount < provider.modelCount
      ? `${provider.enabledCount}/${provider.modelCount} enabled`
      : `${provider.modelCount} models`;

  return html`
    <div
      style="padding:10px 12px;border:1px solid var(--border);border-radius:6px;margin-bottom:6px;cursor:pointer;transition:border-color .15s;
             ${isSelected ? "border-color:var(--accent);background:var(--bg-hover,rgba(128,128,128,.06));" : ""}"
      @click=${() => props.onSelectProvider(provider.id)}
    >
      <div
        style="display:flex;justify-content:space-between;align-items:center;"
      >
        <div>
          <strong>${provider.name}</strong>
          ${
            provider.local
              ? html`
                  <span class="muted" style="font-size: 0.75em; margin-left: 4px">(local)</span>
                `
              : nothing
          }
        </div>
        <span class="pill pill--sm pill--ok">${badge}</span>
      </div>
      ${
        provider.description
          ? html`<div class="muted" style="font-size:0.8em;margin-top:2px;">
            ${provider.description}
          </div>`
          : nothing
      }
    </div>
  `;
}

// --------------- Provider Detail ---------------

function renderProviderDetail(props: ModelsAdminProps) {
  const p = props.selectedProvider!;
  const models = props.selectedProviderModels;
  const enabledCount = models.filter((m) => m.enabled).length;

  return html`
    <div
      style="display:flex;justify-content:space-between;align-items:flex-start;"
    >
      <div>
        <div class="card-title">${p.name}</div>
        <div class="muted" style="font-size:0.85em;">${p.description}</div>
      </div>
      <div style="display:flex;gap:6px;">
        <button
          class="btn btn--sm btn--primary"
          @click=${() => props.onStartEdit(p.id)}
        >
          ${p.connected ? "Update Key" : "Connect"}
        </button>
        ${
          p.connected
            ? html`<button
              class="btn btn--sm"
              style="color:var(--danger,#e55);"
              @click=${() => props.onDisconnect(p.id)}
            >
              Disconnect
            </button>`
            : nothing
        }
      </div>
    </div>

    <div class="detail-stat-grid" style="margin:12px 0;">
      <div class="stat">
        <div class="stat-value">
          ${p.connected ? "Connected" : "Disconnected"}
        </div>
        <div class="stat-label">Status</div>
      </div>
      <div class="stat">
        <div class="stat-value">${p.modelCount}</div>
        <div class="stat-label">Total Models</div>
      </div>
      <div class="stat">
        <div class="stat-value">${enabledCount}</div>
        <div class="stat-label">Enabled</div>
      </div>
      <div class="stat">
        <div class="stat-value">${p.apiProtocol}</div>
        <div class="stat-label">Protocol</div>
      </div>
    </div>

    <div style="font-size:0.85em;margin-bottom:12px;" class="muted">
      Environment variable: <code>${p.envVar}</code>
    </div>

    ${
      models.length > 0
        ? html`
          <div
            style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;"
          >
            <strong>Models (${enabledCount}/${models.length} enabled)</strong>
            <div style="display:flex;gap:6px;">
              <button class="btn btn--sm" @click=${props.onEnableAll}>
                Enable All
              </button>
              <button class="btn btn--sm" @click=${props.onDisableAll}>
                Disable All
              </button>
            </div>
          </div>
          <div style="max-height:400px;overflow-y:auto;">
            <table
              style="width:100%;font-size:0.85em;border-collapse:collapse;"
            >
              <thead>
                <tr style="border-bottom:1px solid var(--border);">
                  <th style="text-align:center;padding:6px;width:40px;">On</th>
                  <th style="text-align:left;padding:6px 8px;">Model</th>
                  <th style="text-align:right;padding:6px 8px;">Context</th>
                  <th style="text-align:center;padding:6px 8px;">Reasoning</th>
                </tr>
              </thead>
              <tbody>
                ${models.map((m) => {
                  const fullId = `${m.provider}/${m.id}`;
                  return html`
                    <tr
                      style="border-bottom:1px solid var(--border);${!m.enabled ? "opacity:0.45;" : ""}"
                    >
                      <td style="text-align:center;padding:6px;">
                        <input
                          type="checkbox"
                          .checked=${m.enabled}
                          @change=${() => props.onToggleModel(fullId)}
                        />
                      </td>
                      <td style="padding:6px 8px;">
                        <strong>${m.name}</strong>
                        <div class="muted" style="font-size:0.85em;">
                          ${m.id}
                        </div>
                      </td>
                      <td style="text-align:right;padding:6px 8px;">
                        ${m.contextWindow ? `${Math.round(m.contextWindow / 1000)}k` : "--"}
                      </td>
                      <td style="text-align:center;padding:6px 8px;">
                        ${m.reasoning ? "Yes" : "--"}
                      </td>
                    </tr>
                  `;
                })}
              </tbody>
            </table>
          </div>
        `
        : html`
            <div class="muted" style="padding: 20px; text-align: center; font-size: 0.9em">
              No models available. Connect this provider to load its models.
            </div>
          `
    }
  `;
}

// --------------- Provider Form ---------------

const URL_PROVIDER_IDS = new Set(["ollama", "lmstudio", "huggingface"]);
const LOCAL_PROVIDER_IDS = new Set(["ollama", "lmstudio"]);
const MODEL_ID_PROVIDER_IDS = new Set(["custom", "huggingface", "lmstudio"]);

function renderProviderForm(props: ModelsAdminProps) {
  const form = props.form!;
  const isNew = form.isNew;
  const showBaseUrl = form.providerId === "custom" || URL_PROVIDER_IDS.has(form.providerId);
  const showCustomFields = form.providerId === "custom" && isNew;
  const showModelIds = MODEL_ID_PROVIDER_IDS.has(form.providerId);
  const inputStyle =
    "width:100%;padding:8px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);margin-top:4px;";

  return html`
    <div class="card-title">
      ${isNew ? "Add LLM Provider" : `Update ${form.name}`}
    </div>

    <div
      style="display:flex;flex-direction:column;gap:14px;margin-top:14px;"
    >
      ${
        isNew
          ? html`
            <label>
              <strong>Provider</strong>
              <select
                .value=${form.providerId}
                @change=${(e: Event) =>
                  props.onFormTypeChange((e.target as HTMLSelectElement).value)}
                style=${inputStyle}
              >
                <option value="">-- Select Provider --</option>
                ${props.addableProviders.map(
                  (p) => html`
                    <option
                      value=${p.id}
                      ?selected=${p.id === form.providerId}
                    >
                      ${p.name} -- ${p.description}
                    </option>
                  `,
                )}
                <option
                  value="custom"
                  ?selected=${form.providerId === "custom"}
                >
                  Custom (OpenAI-compatible)
                </option>
              </select>
            </label>
          `
          : nothing
      }
      ${
        showCustomFields
          ? html`
            <label>
              <strong>Provider Name</strong>
              <input
                type="text"
                .value=${form.name}
                @input=${(e: Event) =>
                  props.onFormChange("name", (e.target as HTMLInputElement).value)}
                style=${inputStyle}
                placeholder="My Provider"
              />
            </label>
            <label>
              <strong>Environment Variable</strong>
              <input
                type="text"
                .value=${form.envVar}
                @input=${(e: Event) =>
                  props.onFormChange("envVar", (e.target as HTMLInputElement).value)}
                style=${inputStyle}
                placeholder="MY_PROVIDER_API_KEY"
              />
            </label>
          `
          : nothing
      }

      <label>
        <strong>API Key</strong>
        <input
          type="password"
          .value=${form.apiKey}
          @input=${(e: Event) => props.onFormChange("apiKey", (e.target as HTMLInputElement).value)}
          style=${inputStyle}
          placeholder=${LOCAL_PROVIDER_IDS.has(form.providerId) ? "(optional for local)" : "sk-... or API key"}
          autocomplete="off"
        />
      </label>

      ${
        showBaseUrl
          ? html`
            <label>
              <strong>Base URL</strong>
              <input
                type="text"
                .value=${form.baseUrl}
                @input=${(e: Event) =>
                  props.onFormChange("baseUrl", (e.target as HTMLInputElement).value)}
                style=${inputStyle}
                placeholder="http://localhost:1234/v1"
              />
            </label>
          `
          : nothing
      }

      ${
        showModelIds
          ? html`
            <label>
              <strong>Model IDs</strong>
              <div class="muted" style="font-size:0.82em;margin-top:2px;margin-bottom:4px;">
                Enter model IDs to make available (one per line or comma-separated).
                ${
                  form.providerId === "huggingface"
                    ? html`
                        <br />Example: <code>meta-llama/Llama-3.1-8B-Instruct</code>
                      `
                    : nothing
                }
              </div>
              <textarea
                .value=${form.modelIds}
                @input=${(e: Event) =>
                  props.onFormChange("modelIds", (e.target as HTMLTextAreaElement).value)}
                style="${inputStyle}min-height:70px;resize:vertical;font-family:monospace;font-size:0.85em;"
                placeholder=${
                  form.providerId === "huggingface"
                    ? "meta-llama/Llama-3.1-8B-Instruct\nmistralai/Mistral-7B-Instruct-v0.3"
                    : "model-name-1\nmodel-name-2"
                }
              ></textarea>
            </label>
          `
          : nothing
      }

      <div class="muted" style="font-size:0.82em;">
        ${
          form.envVar
            ? html`API key will be saved as <code>${form.envVar}</code> in your
              i-GlooBot config. The gateway will restart to apply the change.`
            : html`
                Select a provider above to continue.
              `
        }
      </div>

      <div style="display:flex;gap:8px;">
        <button
          class="btn btn--primary"
          @click=${props.onSaveForm}
          ?disabled=${!props.formDirty}
        >
          ${isNew ? "Save & Connect" : "Save & Restart"}
        </button>
        <button class="btn" @click=${props.onCancelForm}>Cancel</button>
      </div>
    </div>
  `;
}
